import { CancelTokenSource } from 'axios';

import { makeRestApiRequest, RestApiRequestConfig } from '~src/httpClient/RestApiService';
import { createRequestBodyBlob, generateBoundaryVal } from '~src/uploadContent/utils/UploadContentRequestHelper';

import { IFileUploadResponse } from '../types/IBundleUpload';

/**
 * Browser client for the monolith house chunked-upload REST API (the same `fileUploads` session API
 * workbook/datasource/flow publishing is built on; W-23797339). A bundle is "just another upload":
 *
 *   initiateFileUpload (POST)            -> { fileUpload: { uploadSessionId } }
 *   appendFileUpload   (PUT, per chunk)  -> appends bytes to that session
 *
 * The caller then references the uploadSessionId on `triggerUpload` so the monolith worker assembles
 * the bundle and hands it to the service through the cell SOS filestore. No bespoke upload code —
 * chunking/resume/quota/checksum/cleanup are the house machinery's job.
 */

const REST_API_VERSION = '3.6';
const FILE_UPLOADS_ENDPOINT = 'fileUploads';

/** The multipart part name the house upload expects for the file bytes. */
const TABLEAU_FILE_PART = 'tableau_file';

/** The append body's (empty) `request_payload` part, ahead of the chunk bytes. */
const EMPTY_REQUEST_PAYLOAD_PART = 'Content-Disposition: name="request_payload"\r\nContent-Type: text/xml\r\n\r\n\r\n';

/** POST an empty-bodied initiate request; returns the new upload session. */
export function initiateFileUpload(cancelToken?: CancelTokenSource): Promise<IFileUploadResponse> {
  const config: RestApiRequestConfig<null> = {
    restApiVersion: REST_API_VERSION,
    httpMethod: 'POST',
    endpoint: FILE_UPLOADS_ENDPOINT,
    requestBody: null,
    cancelToken,
  };
  return makeRestApiRequest<null, IFileUploadResponse>(config);
}

/**
 * PUT one chunk onto an existing upload session. Progress + cancel flow straight through to axios so
 * the dialog can aggregate a cross-chunk progress bar and abort an in-flight upload.
 */
export function appendFileUpload(
  uploadSessionId: string,
  chunk: Blob,
  onUploadProgress?: (progressEvent: unknown) => void,
  cancelToken?: CancelTokenSource
): Promise<IFileUploadResponse> {
  const boundaryVal = generateBoundaryVal();
  const config: RestApiRequestConfig<Blob> = {
    restApiVersion: REST_API_VERSION,
    httpMethod: 'PUT',
    endpoint: `${FILE_UPLOADS_ENDPOINT}/${uploadSessionId}`,
    // A chunk has no file name of its own, so the part is named after the multipart field.
    requestBody: createRequestBodyBlob(TABLEAU_FILE_PART, boundaryVal, chunk, EMPTY_REQUEST_PAYLOAD_PART, TABLEAU_FILE_PART),
    headers: { 'Content-Type': `multipart/mixed; boundary=${boundaryVal}` },
    onUploadProgress,
    cancelToken,
  };
  return makeRestApiRequest<Blob, IFileUploadResponse>(config);
}
